'use client'

import { useMemo, useState } from 'react'

import { CARD_TITLE } from '@/lib/typography'

import { SectionHeading } from '@/components/sections/section-stats'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { MaybeHtml } from '@/components/blocks/maybe-html'
import { MongoliaMap } from '@/components/blocks/mongolia-map'
import { MONGOLIA_CAPITAL } from '@/lib/mongolia-map'
import { useDictionary } from '@/lib/i18n/provider'
import type { Section } from '@/types/website'
import { SECTION_PADDING, SITE_CONTAINER } from '@/components/layout/container'
import { cn } from '@/lib/utils'
import { PillButton } from '@/components/blocks/pill-button'

type CoverageRow = {
  province?: string
  value?: string
  contracted?: string
  text?: string
}

const toNumber = (v?: string) => Number(String(v ?? '').replace(/[^\d.-]/g, '')) || 0
const format = (n: number) => n.toLocaleString('en-US')

/**
 * Борлуулалтын цар хүрээ — аймаг бүрийн харилцагчийн тоо газрын зураг дээр.
 *
 * Бүтэц нь захиалагчийн зааврын дагуу: дээд талд хүчтэй тоо, доор нь
 * газарзүйн хамрах хүрээ, хамгийн доор нэг өгүүлбэрийн нотолгоо.
 *
 * Нийт дүнг мөрүүдээс ӨӨРӨӨ тооцно — гараар давхар бичүүлбэл мөр нэмэх
 * бүрд нийлбэр хоцорч, хуудас өөртэйгөө зөрчилдөнө. Зөвхөн нийслэлийн
 * дүүргийн тоо аймгийн мөрөөс гарахгүй тул тохиргооноос ирнэ.
 *
 * ⚠️ Client component: зурмал (~36 KB gzip) хөтөч рүү очно. Серверт зурж
 * зөвхөн харилцан үйлдлийг хуваах боломжтой ч сонгосон аймгийг тодруулахад
 * зурмалд дахин хандах шаардлагатай болдог.
 */
export const SectionCoverage = ({ section }: { section: Section }) => {
  const t = useDictionary()

  const items = section.items
  const rows = useMemo(() => (items ?? []) as CoverageRow[], [items])

  const byProvince = useMemo(() => {
    const map = new Map<string, CoverageRow>()

    for (const row of rows) {
      if (row.province) map.set(row.province, row)
    }

    return map
  }, [rows])

  const totals = useMemo(() => {
    const customers = rows.reduce((sum, row) => sum + toNumber(row.value), 0)
    const contracted = rows.reduce((sum, row) => sum + toNumber(row.contracted), 0)

    /* Цэгийн радиусыг харьцуулах суурь — хамгийн том утга */
    const sorted = rows
      .map(row => toNumber(row.value))
      .filter(v => v > 0)
      .sort((a, b) => a - b)

    /*
     * Улаанбаатар нь АЙМАГ БИШ. Бүх мөрийг тоолвол «22 аймаг» гэж гарч,
     * дэргэдэх «21 аймаг, Улаанбаатарын 9 дүүрэг» гэсэн өгүүлбэртэйгээ
     * зөрчилдөнө.
     */
    const aimags = [...byProvince.keys()].filter(name => name !== MONGOLIA_CAPITAL).length

    // Цэгийн радиусыг хамгийн том утгад харьцуулж тооцно.
    const max = sorted[sorted.length - 1] ?? 1

    return { customers, contracted, aimags, max }
  }, [rows, byProvince])

  /** Дэлгэрэнгүй хүснэгт — олноос цөөн рүү */
  const ranked = useMemo(
    () => rows.filter(row => row.province).sort((a, b) => toNumber(b.value) - toNumber(a.value)),
    [rows]
  )

  const [selected, setSelected] = useState<string | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const active = selected ? byProvince.get(selected) : null

  if (rows.length === 0) return null

  const districts = String(section.settings.districts ?? '').trim()
  const footnote = String(section.settings.footnote ?? '').trim()

  const stats = [
    { value: format(totals.aimags), label: t.coverage.province },
    ...(districts ? [{ value: districts, label: t.coverage.district }] : []),
    { value: format(totals.customers), label: t.coverage.customer },
    ...(totals.contracted > 0 ? [{ value: format(totals.contracted), label: t.coverage.contracted }] : [])
  ]

  return (
    <section>
      <div className={cn(SITE_CONTAINER, SECTION_PADDING)}>
        <SectionHeading eyebrow={section.eyebrow} heading={section.heading} subheading={section.subheading} />

        {/*
          Газрын зураг ба тоон самбар НЭГ хэсэгт.

          Урьд нь тоонууд зүүн тусдаа баганад сууж, зурагтай ямар ч холбоогүй
          мэт харагддаг байв. Одоо самбар нь зургийн ЗҮҮН талд хөвж, аймаг
          дээр дархад тухайн аймгийн тоо руу солигдоно. Зурмагийг `lg`-ээс
          дээш зүүн талаас нь ухраасан тул самбар нь баруун зүгийн аймгуудыг
          хаахгүй.

          Самбар нь ХҮРЭЭ, ДЭВСГЭРГҮЙ: зургийг халхлахгүй тул хайрцаглах
          шаардлагагүй бөгөөд хоосон дэвсгэр нь хуудсыг цэвэрхэн болгоно.
        */}
        <div className='relative mt-10'>
          <div className='lg:ps-[21rem] xl:ps-[23rem]'>
            <MongoliaMap
              selectable={new Set(byProvince.keys())}
              selected={selected}
              onSelect={setSelected}
              labelOf={name => {
                const row = byProvince.get(name)

                return row ? `${name} — ${format(toNumber(row.value))} харилцагч` : name
              }}
            />
          </div>

          <div className='mt-8 rounded-2xl p-6 lg:absolute lg:top-1/2 lg:left-0 lg:mt-0 lg:w-[20rem] lg:-translate-y-1/2 lg:p-7 xl:w-[22rem]'>
            {active ? (
              <div className='flex flex-col gap-5'>
                <div className='flex items-start justify-between gap-4'>
                  <div>
                    <p className='text-muted-foreground text-xl font-medium tracking-widest uppercase'>Сонгосон бүс</p>
                    <h3 className={cn('mt-1', CARD_TITLE)}>{active.province}</h3>
                  </div>
                  <button
                    type='button'
                    onClick={() => setSelected(null)}
                    className='text-muted-foreground hover:text-foreground -mt-1 text-2xl leading-none'
                    aria-label={t.common.closeSelection}
                  >
                    ×
                  </button>
                </div>

                <dl className='flex flex-wrap gap-x-10 gap-y-4'>
                  <div>
                    <dd className='text-4xl font-bold tracking-tight tabular-nums md:text-5xl'>
                      {format(toNumber(active.value))}
                    </dd>
                    <dt className='text-muted-foreground mt-1 text-xl font-medium tracking-widest uppercase'>
                      {t.coverage.customer}
                    </dt>
                  </div>
                  {toNumber(active.contracted) > 0 && (
                    <div>
                      <dd className='text-4xl font-bold tracking-tight tabular-nums md:text-5xl'>
                        {format(toNumber(active.contracted))}
                      </dd>
                      <dt className='text-muted-foreground mt-1 text-xl font-medium tracking-widest uppercase'>
                        {t.coverage.contracted}
                      </dt>
                    </div>
                  )}
                </dl>

                {active.text && <p className='text-muted-foreground text-xl text-pretty'>{active.text}</p>}
              </div>
            ) : (
              <div className='flex flex-col gap-5'>
                <p className='text-muted-foreground text-xl font-medium tracking-widest uppercase'>{t.coverage.total}</p>

                <dl className='grid grid-cols-2 gap-x-8 gap-y-5'>
                  {stats.map(stat => (
                    <div key={stat.label}>
                      <dd className='text-4xl font-bold tracking-tight tabular-nums md:text-5xl'>{stat.value}</dd>
                      <dt className='text-muted-foreground mt-1 text-xl font-medium tracking-widest uppercase'>
                        {stat.label}
                      </dt>
                    </div>
                  ))}
                </dl>

                <MaybeHtml text={footnote} className='text-muted-foreground text-justify text-xl hyphens-auto' />

                <p className='text-muted-foreground text-xl'>{t.coverage.hint}</p>
              </div>
            )}

            {/*
              Газрын зураг нь ерөнхий дүр зургийг өгнө; бүх аймгийг тоогоор нь
              зэрэгцүүлж харах хэрэгтэй хүнд хүснэгт хэрэгтэй.
            */}
            <PillButton className='mt-6' onClick={() => setDetailOpen(true)}>
              {t.common.details}
            </PillButton>
          </div>
        </div>

        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className='max-h-[85vh] overflow-hidden p-8 sm:max-w-2xl'>
            <DialogHeader>
              <DialogTitle>{section.heading || 'Борлуулалтын цар хүрээ'}</DialogTitle>
              <DialogDescription>
                {format(totals.aimags)} аймаг, нийслэл · {format(totals.customers)} харилцагч
                {totals.contracted > 0 && ` · ${format(totals.contracted)} гэрээт`}
              </DialogDescription>
            </DialogHeader>

            <div className='-mx-8 max-h-[60vh] overflow-y-auto px-8'>
              <table className='w-full text-xl'>
                <thead className='bg-background sticky top-0'>
                  <tr className='border-b'>
                    <th className='py-2 text-left font-medium'>Бүс</th>
                    <th className='py-2 text-right font-medium'>{t.coverage.customer}</th>
                    {totals.contracted > 0 && <th className='py-2 text-right font-medium'>{t.coverage.contracted}</th>}
                  </tr>
                </thead>
                <tbody>
                  {ranked.map(row => {
                    const customers = toNumber(row.value)
                    const contracted = toNumber(row.contracted)

                    return (
                      <tr key={row.province} className='border-b last:border-0'>
                        <td className='py-2'>{row.province}</td>
                        <td className='py-2 text-right tabular-nums'>{format(customers)}</td>
                        {totals.contracted > 0 && (
                          <td className='py-2 text-right tabular-nums'>{format(contracted)}</td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className='border-t font-medium'>
                    <td className='py-2'>Нийт</td>
                    <td className='py-2 text-right tabular-nums'>{format(totals.customers)}</td>
                    {totals.contracted > 0 && (
                      <td className='py-2 text-right tabular-nums'>{format(totals.contracted)}</td>
                    )}
                  </tr>
                </tfoot>
              </table>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  )
}
